/**
 * System Data > Power of Attorney Roles, classic look. The "Which roles"
 * list: add, rename, switch off or back on (never delete). Logic and texts:
 * usePowerOfAttorneyRoles.
 *
 * IMT copy of HMS 2e421ecb, drawn with IMT's own HMSTable, HMSModal and the
 * dashboard's form-group field (IMT has no ui/fields yet).
 */
import React from 'react';
import HMSButton from '../ui/HMSButton';
import HMSModal from '../ui/HMSModal';
import HMSTable from '../ui/HMSTable';
import usePowerOfAttorneyRoles from './usePowerOfAttorneyRoles';
import './hr.css';

const COLUMNS = [
  { key: 'name', label: 'Role' },
  { key: 'is_active', label: 'Status' },
  { key: 'in_use_count', label: 'Used on' },
  { key: 'actions', label: 'Actions', align: 'right' },
];

export default function PowerOfAttorneyRolesPanel() {
  const r = usePowerOfAttorneyRoles();

  const renderCell = (role, column) => {
    if (column.key === 'is_active') {
      return (
        <span className={`hr-attorney-status hr-attorney-status--${role.is_active ? 'active' : 'expired'}`}>
          {role.is_active ? 'On' : 'Switched off'}
        </span>
      );
    }
    if (column.key === 'in_use_count') {
      return `${role.in_use_count || 0} record${role.in_use_count === 1 ? '' : 's'}`;
    }
    if (column.key === 'actions') {
      return (
        <div className="hr-attorney-row-actions">
          <HMSButton variant="ghost" size="compact" onClick={() => r.openRename(role)}>Rename</HMSButton>
          {role.is_active ? (
            <HMSButton variant="dangerGhost" size="compact" onClick={() => r.setActive(role, false)}>Switch off</HMSButton>
          ) : (
            <HMSButton variant="ghost" size="compact" onClick={() => r.setActive(role, true)}>Switch on</HMSButton>
          )}
        </div>
      );
    }
    return role[column.key];
  };

  return (
    <div className="hr-attorney">
      <div className="hr-attorney-toolbar">
        <span className="hr-attorney-muted">
          The roles offered under "Which roles" when adding a power of attorney. Switch a role off to stop
          offering it; records that already name it keep it.
        </span>
        <HMSButton onClick={r.openNew}>Add Role</HMSButton>
      </div>

      {r.loadError ? <p className="hr-attorney-error" role="alert">{r.loadError}</p> : null}

      {r.loading ? (
        <p className="hr-attorney-muted" role="status">Loading roles...</p>
      ) : r.roles.length === 0 ? (
        <p className="hr-attorney-muted">No roles yet. Add the first one with "Add Role".</p>
      ) : (
        <HMSTable columns={COLUMNS} rows={r.roles} rowKey={(role) => role.id} renderCell={renderCell} />
      )}

      <HMSModal
        isOpen={Boolean(r.editing)}
        onClose={r.closeForm}
        title={r.editing === 'new' ? 'Add Power of Attorney Role' : 'Rename Power of Attorney Role'}
        width={480}
        showClose
        busy={r.saving}
        footer={(
          <>
            <HMSButton variant="secondary" onClick={r.closeForm} disabled={r.saving}>Cancel</HMSButton>
            <HMSButton onClick={r.save} disabled={r.saving}>{r.saving ? 'Saving...' : 'Save'}</HMSButton>
          </>
        )}
      >
        <div className="form-group">
          <label htmlFor="attorney-role-name">Role name *</label>
          <input
            id="attorney-role-name"
            type="text"
            className="form-input"
            required
            value={r.name}
            maxLength={100}
            onChange={(e) => r.setName(e.target.value)}
          />
        </div>
        {r.formError ? <p className="hr-attorney-error" role="alert">{r.formError}</p> : null}
      </HMSModal>
    </div>
  );
}
